import { useState, useMemo } from 'react';
import type { JSX } from 'react';
import { Badge, Button, Card, DatePicker, Grid, Input, Pagination, Select, Space, Table, Tag, Typography, theme } from 'antd';
import { ClearOutlined, EyeOutlined, FileTextOutlined, FilterOutlined, SearchOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import type { ColumnsType } from 'antd/es/table';

import type { ClinicalRecord, ClinicalRecordFilters, ClinicalRecordStatus } from '../api/clinicalHistory.api';
import type { StaffDirectoryEntry } from '@erp/services/api/serviceStaff';
import { formatServiceLineLabel } from '../utils/serviceLineLabel';
import { buildClinicalRecordNotePreview } from '@erp/appointments/components/history/historyHelpers';

const { Text } = Typography;
const { RangePicker } = DatePicker;

const STATUS_LABELS: Record<string, string> = {
  draft: 'Borrador',
  scheduled: 'Agendado',
  attended: 'Atendido',
  no_show: 'Ausente',
  canceled: 'Cancelado',
};

const STATUS_COLORS: Record<string, string> = {
  draft: 'default',
  scheduled: 'blue',
  attended: 'green',
  no_show: 'volcano',
  canceled: 'red',
};

const STATUS_OPTIONS: Array<{ value: ClinicalRecordStatus; label: string }> = [
  { value: 'draft', label: 'Borrador' },
  { value: 'scheduled', label: 'Agendado' },
  { value: 'attended', label: 'Atendido' },
  { value: 'no_show', label: 'Ausente' },
  { value: 'canceled', label: 'Cancelado' },
];

const HISTORY_STATUS_OPTIONS = [
  { value: 'completed', label: 'Completada' },
  { value: 'pending', label: 'Sin historia' },
  { value: 'all', label: 'Todas' },
];

type ColumnFilterHeaderProps = {
  label: string;
  children?: React.ReactNode;
};

const ColumnFilterHeader: React.FC<ColumnFilterHeaderProps> = ({ label, children }) => (
  <div style={{ display: 'flex', flexDirection: 'column', gap: 4, padding: '4px 0' }}>
    <span style={{ fontWeight: 600 }}>{label}</span>
    {children ? <div onClick={(e) => e.stopPropagation()}>{children}</div> : null}
  </div>
);

export interface ClinicalHistoryTableProps {
  records: ClinicalRecord[];
  loading: boolean;
  total: number;
  page: number;
  pageSize: number;
  filters: ClinicalRecordFilters;
  staffDirectory: StaffDirectoryEntry[];
  onFiltersChange: (filters: ClinicalRecordFilters) => void;
  onResetFilters: () => void;
  onPageChange: (page: number, pageSize: number) => void;
  onViewRecord: (recordId: number) => void;
}

/**
 * Objetivo: Mostrar un listado denso de historias clínicas adaptado responsivamente (Tabla en Desktop, Tarjetas Táctiles en Mobile).
 */
export function ClinicalHistoryTable({
  records,
  loading,
  total,
  page,
  pageSize,
  filters,
  staffDirectory,
  onFiltersChange,
  onResetFilters,
  onPageChange,
  onViewRecord,
}: ClinicalHistoryTableProps): JSX.Element {
  const { token } = theme.useToken();
  const screens = Grid.useBreakpoint();
  const isMobile = !screens.md;

  const [isMobileFiltersExpanded, setIsMobileFiltersExpanded] = useState(false);

  const activeFilterCount = useMemo(() => {
    let count = 0;
    if (filters.start || filters.end) count++;
    if (filters.customer_search) count++;
    if (filters.staff_user_id) count++;
    if (filters.status) count++;
    if (filters.service_search) count++;
    if (filters.history_status && filters.history_status !== 'completed') count++;
    return count;
  }, [filters]);

  const hasActiveFilters = activeFilterCount > 0;

  const staffOptions = useMemo(
    () =>
      staffDirectory.map((staff) => {
        const parts = [staff.first_name, staff.last_name].filter((p) => p && p.trim().length > 0);
        const label = parts.length > 0 ? parts.join(' ') : staff.username || `Staff #${staff.id}`;
        return { value: staff.id, label };
      }),
    [staffDirectory],
  );

  const initialDateRange: [dayjs.Dayjs, dayjs.Dayjs] | undefined =
    filters.start && filters.end
      ? [dayjs(filters.start), dayjs(filters.end)]
      : undefined;

  const columns: ColumnsType<ClinicalRecord> = [
    {
      title: (
        <ColumnFilterHeader label="Fecha y Hora">
          <RangePicker
            size="small"
            style={{ width: '100%', maxWidth: 210 }}
            format="DD/MM/YY"
            placeholder={['Desde', 'Hasta']}
            value={initialDateRange}
            onChange={(dates) =>
              onFiltersChange({
                ...filters,
                start: dates?.[0]?.toISOString(),
                end: dates?.[1]?.toISOString(),
                page: 1,
              })
            }
          />
        </ColumnFilterHeader>
      ),
      dataIndex: 'start_at',
      key: 'start_at',
      width: 220,
      render: (start_at: string) => (
        <Text style={{ whiteSpace: 'nowrap' }}>{dayjs(start_at).format('DD/MM/YYYY HH:mm')}</Text>
      ),
    },
    {
      title: (
        <ColumnFilterHeader label="Paciente">
          <Input
            size="small"
            allowClear
            prefix={<SearchOutlined />}
            placeholder="Buscar..."
            value={filters.customer_search ?? ''}
            onChange={(e) =>
              onFiltersChange({
                ...filters,
                customer_search: e.target.value || undefined,
                page: 1,
              })
            }
          />
        </ColumnFilterHeader>
      ),
      key: 'customer',
      width: 180,
      render: (_, record) => (
        <Space orientation="vertical" size={0}>
          <Text strong>[{record.customer_id}] {record.customer_name || record.customer_username || 'Sin asignar'}</Text>
          {/*{record.customer_username && record.customer_name ? (*/}
          {/*  <Text type="secondary" style={{ fontSize: 12 }}>*/}
          {/*    @{record.customer_username}*/}
          {/*  </Text>*/}
          {/*) : null}*/}
        </Space>
      ),
    },
    {
      title: (
        <ColumnFilterHeader label="Profesional">
          <Select
            size="small"
            allowClear
            showSearch
            style={{ width: '100%' }}
            placeholder="Todos"
            value={filters.staff_user_id}
            options={staffOptions}
            filterOption={(input, option) =>
              (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
            }
            onChange={(value) =>
              onFiltersChange({
                ...filters,
                staff_user_id: value,
                page: 1,
              })
            }
          />
        </ColumnFilterHeader>
      ),
      key: 'staff',
      width: 160,
      render: (_, record) => (
        <Text>{record.staff_name || `Staff #${record.staff_user_id}`}</Text>
      ),
    },
    {
      title: (
        <ColumnFilterHeader label="Servicios">
          <Input
            size="small"
            allowClear
            prefix={<SearchOutlined />}
            placeholder="Buscar..."
            value={filters.service_search ?? ''}
            onChange={(e) =>
              onFiltersChange({
                ...filters,
                service_search: e.target.value || undefined,
                page: 1,
              })
            }
          />
        </ColumnFilterHeader>
      ),
      key: 'services',
      width: 180,
      render: (_, record) => {
        const serviceNames = record.service_lines.map(formatServiceLineLabel);
        return <Text>{serviceNames.join(', ')}</Text>;
      },
    },
    {
      title: (
        <ColumnFilterHeader label="Estado">
          <Select
            size="small"
            allowClear
            style={{ width: '100%' }}
            placeholder="Todos"
            value={filters.status}
            options={STATUS_OPTIONS}
            onChange={(value) =>
              onFiltersChange({
                ...filters,
                status: value,
                page: 1,
              })
            }
          />
        </ColumnFilterHeader>
      ),
      dataIndex: 'status',
      key: 'status',
      width: 120,
      render: (status: string) => (
        <Tag color={STATUS_COLORS[status] || 'default'}>
          {STATUS_LABELS[status] || status}
        </Tag>
      ),
    },
    {
      title: (
        <ColumnFilterHeader label="Historia Clínica">
          <Select
            size="small"
            style={{ width: '100%' }}
            value={filters.history_status ?? 'completed'}
            options={HISTORY_STATUS_OPTIONS}
            onChange={(value) =>
              onFiltersChange({
                ...filters,
                history_status: value,
                page: 1,
              })
            }
          />
        </ColumnFilterHeader>
      ),
      key: 'latest_history_note',
      width: 280,
      render: (_, record) => {
        const preview = record.latest_history_note ? buildClinicalRecordNotePreview(record) : null;
        return preview ? (
          <Text
            type="secondary"
            style={{
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              fontSize: 12,
              color: token.colorTextSecondary,
            }}
            title={preview}
          >
            {preview}
          </Text>
        ) : (
          <Text type="secondary" style={{ fontSize: 12, fontStyle: 'italic' }}>
            Sin historia registrada
          </Text>
        );
      },
    },
    {
      title: 'Registros',
      dataIndex: 'history_entries_count',
      key: 'history_entries_count',
      width: 100,
      align: 'center',
      render: (count: number) => (
        <Badge
          count={count}
          showZero
          color={count > 0 ? 'blue' : 'default'}
          overflowCount={99}
        />
      ),
    },
    {
      title: 'Acciones',
      key: 'actions',
      width: 90,
      align: 'center',
      fixed: 'right',
      render: (_, record) => (
        <Button
          type="text"
          icon={<EyeOutlined />}
          onClick={() => onViewRecord(record.id)}
          title="Ver historia clínica"
        />
      ),
    },
  ];

  const renderMobileCard = (record: ClinicalRecord) => {
    const preview = record.latest_history_note ? buildClinicalRecordNotePreview(record) : null;
    const servicesLabel = record.service_lines.length > 0
      ? record.service_lines.map(formatServiceLineLabel).join(', ')
      : 'Sin servicio';

    return (
      <Card
        key={record.id}
        size="small"
        style={{
          borderRadius: 10,
          borderColor: token.colorBorderSecondary,
          backgroundColor: token.colorBgContainer,
          cursor: 'pointer',
          transition: 'all 150ms ease',
        }}
        onClick={() => onViewRecord(record.id)}
      >
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-start',
            gap: 8,
            marginBottom: 6,
          }}
        >
          <div>
            <Text strong style={{ fontSize: 14, display: 'block', textAlign: 'left' }}>
              [{record.customer_id}]{' '}
              {record.customer_name ||
                record.customer_username ||
                'Sin asignar'}
            </Text>
            {/*{record.customer_username && record.customer_name ? (*/}
            {/*  <Text type="secondary" style={{ fontSize: 11 }}>*/}
            {/*    @{record.customer_username}*/}
            {/*  </Text>*/}
            {/*) : null}*/}
          </div>
          <Tag color="blue" style={{ margin: 0, fontSize: 11 }}>
            {dayjs(record.start_at).format('DD/MM HH:mm')} hs
          </Tag>
          <Tag color={STATUS_COLORS[record.status] || 'default'}>
            {STATUS_LABELS[record.status] || record.status}
          </Tag>
        </div>

        <Space
          size={4}
          wrap
          style={{
            marginBottom: 8,
            justifyContent: 'space-between',
            width: '100%',
          }}
        >
          <Tag color="cyan">
            {record.staff_name || `Staff #${record.staff_user_id}`}
          </Tag>
          <Space size={6}>
            <Text type="secondary" style={{ fontSize: 11 }}>
              Registros:
            </Text>
            <Badge
              count={record.history_entries_count}
              showZero
              color={record.history_entries_count > 0 ? 'blue' : 'default'}
              overflowCount={99}
            />
            <Button
              type="text"
              size="small"
              icon={<EyeOutlined />}
              onClick={(e) => {
                e.stopPropagation();
                onViewRecord(record.id);
              }}
            />
          </Space>
        </Space>

        <Space
          size={8}
          wrap
          style={{ marginBottom: 8, justifyContent: 'start', width: '100%' }}
        >
          <Text type="secondary" style={{ fontSize: 11, display: 'block' }}>
            Servicios:
          </Text>
          <Text style={{ fontSize: 12 }}>{servicesLabel}</Text>
        </Space>

        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            paddingTop: 6,
            borderTop: `1px dashed ${token.colorBorderSecondary}`,
          }}
        >
          {preview ? (
            <div
              style={{
                padding: '6px 10px',
                backgroundColor: token.colorFillAlter,
                borderRadius: 6,
                borderLeft: `3px solid ${token.colorPrimary}`,
                marginBottom: 8,
                width: '100%',
              }}
            >
              <Text
                type="secondary"
                style={{
                  fontSize: 12,
                  display: '-webkit-box',
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: 'vertical',
                  overflow: 'hidden',
                  textAlign: 'left',
                }}
              >
                {preview}
              </Text>
            </div>
          ) : null}
        </div>
      </Card>
    );
  };

  return (
    <Space orientation="vertical" style={{ width: '100%' }} size="middle">
      {/* Barra de Herramientas y Filtros */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8 }}>
        <Text type="secondary" style={{ fontSize: 13 }}>
          {hasActiveFilters ? 'Filtros personalizados aplicados' : 'Mostrando por defecto historias completadas'}
        </Text>

        <Space size="small">
          {isMobile ? (
            <Button
              size="small"
              type={isMobileFiltersExpanded || activeFilterCount > 0 ? 'primary' : 'default'}
              ghost={isMobileFiltersExpanded || activeFilterCount > 0}
              icon={<FilterOutlined />}
              onClick={() => setIsMobileFiltersExpanded((prev) => !prev)}
            >
              Filtros{activeFilterCount > 0 ? ` (${activeFilterCount})` : ''}
            </Button>
          ) : null}

          <Button
            size="small"
            icon={<ClearOutlined />}
            type={hasActiveFilters ? 'primary' : 'default'}
            danger={hasActiveFilters}
            ghost={hasActiveFilters}
            disabled={!hasActiveFilters}
            onClick={onResetFilters}
          >
            Limpiar filtros
          </Button>
        </Space>
      </div>

      {/* Panel Desplegable de Filtros para Mobile */}
      {isMobile && isMobileFiltersExpanded ? (
        <Card size="small" style={{ backgroundColor: token.colorFillAlter, borderRadius: 8 }}>
          <Space orientation="vertical" size="small" style={{ width: '100%' }}>
            <div>
              <Text type="secondary" style={{ fontSize: 12 }}>Buscar Paciente:</Text>
              <Input
                size="small"
                allowClear
                prefix={<SearchOutlined />}
                placeholder="Nombre o usuario"
                value={filters.customer_search ?? ''}
                onChange={(e) =>
                  onFiltersChange({
                    ...filters,
                    customer_search: e.target.value || undefined,
                    page: 1,
                  })
                }
              />
            </div>

            <div>
              <Text type="secondary" style={{ fontSize: 12 }}>Buscar Servicio:</Text>
              <Input
                size="small"
                allowClear
                prefix={<SearchOutlined />}
                placeholder="Nombre de servicio"
                value={filters.service_search ?? ''}
                onChange={(e) =>
                  onFiltersChange({
                    ...filters,
                    service_search: e.target.value || undefined,
                    page: 1,
                  })
                }
              />
            </div>

            <div>
              <Text type="secondary" style={{ fontSize: 12 }}>Historia Clínica:</Text>
              <Select
                size="small"
                style={{ width: '100%' }}
                value={filters.history_status ?? 'completed'}
                options={HISTORY_STATUS_OPTIONS}
                onChange={(value) =>
                  onFiltersChange({
                    ...filters,
                    history_status: value,
                    page: 1,
                  })
                }
              />
            </div>

            <div>
              <Text type="secondary" style={{ fontSize: 12 }}>Profesional:</Text>
              <Select
                size="small"
                allowClear
                showSearch
                style={{ width: '100%' }}
                placeholder="Todos"
                value={filters.staff_user_id}
                options={staffOptions}
                filterOption={(input, option) =>
                  (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                }
                onChange={(value) =>
                  onFiltersChange({
                    ...filters,
                    staff_user_id: value,
                    page: 1,
                  })
                }
              />
            </div>

            <div>
              <Text type="secondary" style={{ fontSize: 12 }}>Estado Turno:</Text>
              <Select
                size="small"
                allowClear
                style={{ width: '100%' }}
                placeholder="Todos"
                value={filters.status}
                options={STATUS_OPTIONS}
                onChange={(value) =>
                  onFiltersChange({
                    ...filters,
                    status: value,
                    page: 1,
                  })
                }
              />
            </div>

            <div>
              <Text type="secondary" style={{ fontSize: 12 }}>Rango de Fechas:</Text>
              <RangePicker
                size="small"
                style={{ width: '100%' }}
                format="DD/MM/YY"
                placeholder={['Desde', 'Hasta']}
                value={initialDateRange}
                onChange={(dates) =>
                  onFiltersChange({
                    ...filters,
                    start: dates?.[0]?.toISOString(),
                    end: dates?.[1]?.toISOString(),
                    page: 1,
                  })
                }
              />
            </div>
          </Space>
        </Card>
      ) : null}

      {/* Renderizado Condicional: Tarjetas en Mobile, Tabla en Desktop */}
      {isMobile ? (
        <Space orientation="vertical" size="small" style={{ width: '100%' }}>
          {loading ? (
            <Text type="secondary">Cargando historias clínicas...</Text>
          ) : records.length === 0 ? (
            <Card size="small" style={{ textAlign: 'center', padding: '24px 0' }}>
              <FileTextOutlined style={{ fontSize: 36, color: '#bfbfbf' }} />
              <Text type="secondary" style={{ display: 'block', marginTop: 8 }}>
                No se encontraron historias clínicas
              </Text>
            </Card>
          ) : (
            records.map(renderMobileCard)
          )}

          <div style={{ display: 'flex', justifyContent: 'center', marginTop: 12 }}>
            <Pagination
              size="small"
              current={page}
              pageSize={pageSize}
              total={total}
              onChange={onPageChange}
              showSizeChanger={false}
            />
          </div>
        </Space>
      ) : (
        <Table
          columns={columns}
          dataSource={records}
          loading={loading}
          rowKey="id"
          size="small"
          pagination={{
            current: page,
            pageSize,
            total,
            showSizeChanger: true,
            showTotal: (total, range) => `${range[0]}-${range[1]} de ${total} registros`,
            pageSizeOptions: ['20', '50', '100'],
            onChange: onPageChange,
          }}
          locale={{
            emptyText: (
              <Space orientation="vertical" size="small" style={{ padding: '40px 0' }}>
                <FileTextOutlined style={{ fontSize: 48, color: '#bfbfbf' }} />
                <Text type="secondary">No se encontraron historias clínicas</Text>
              </Space>
            ),
          }}
          scroll={{ x: 1200 }}
        />
      )}
    </Space>
  );
}
